import JsSIP from 'jssip';

/**
 * 3CX WebRTC/SIP Service
 * Makes real calls using WebRTC through 3CX SIP
 * No API credentials needed - just your 3CX SIP credentials
 */

class ThreeCXWebRTC {
  constructor() {
    this.ua = null; // User Agent
    this.currentSession = null;
    this.isRegistered = false;
    this.callbacks = {};

    // Configuration from environment
    this.sipServer = process.env.REACT_APP_3CX_SIP_SERVER || 'wss://1726.3cx.cloud:5001';
    this.sipUser = process.env.REACT_APP_3CX_SIP_USER || '';
    this.sipPassword = process.env.REACT_APP_3CX_SIP_PASSWORD || '';
    this.sipExtension = process.env.REACT_APP_3CX_SIP_EXTENSION || '';

    // Disable JsSIP debug logs (enable for troubleshooting)
    JsSIP.debug.disable('JsSIP:*');
    // JsSIP.debug.enable('JsSIP:*'); // Uncomment for debugging
  }

  /**
   * Initialize and register the SIP user agent
   */
  async initialize() {
    if (this.ua) {
      console.log('WebRTC already initialized');
      return true;
    }

    try {
      const socket = new JsSIP.WebSocketInterface(this.sipServer);

      const configuration = {
        sockets: [socket],
        uri: `sip:${this.sipExtension}@${this.sipServer.replace(/^wss?:\/\//, '').split(':')[0]}`,
        password: this.sipPassword,
        display_name: this.sipUser,
        register: true,
        session_timers: false,
      };

      this.ua = new JsSIP.UA(configuration);

      // Set up event handlers
      this.setupEventHandlers();

      // Start the user agent
      this.ua.start();

      // Wait for registration
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Registration timeout'));
        }, 10000);

        this.ua.on('registered', () => {
          clearTimeout(timeout);
          this.isRegistered = true;
          console.log('3CX WebRTC: Registered successfully');
          resolve(true);
        });

        this.ua.on('registrationFailed', (e) => {
          clearTimeout(timeout);
          console.error('3CX WebRTC: Registration failed', e);
          reject(new Error('Registration failed: ' + e.cause));
        });
      });
    } catch (error) {
      console.error('3CX WebRTC initialization error:', error);
      throw error;
    }
  }

  /**
   * Set up event handlers for the user agent
   */
  setupEventHandlers() {
    this.ua.on('connected', () => {
      console.log('3CX WebRTC: Connected to server');
      this.triggerCallback('connected');
    });

    this.ua.on('disconnected', () => {
      console.log('3CX WebRTC: Disconnected from server');
      this.isRegistered = false;
      this.triggerCallback('disconnected');
    });

    this.ua.on('newRTCSession', (data) => {
      const session = data.session;

      if (session.direction === 'incoming') {
        // Incoming call
        console.log('3CX WebRTC: Incoming call from', session.remote_identity.uri.user);
        this.currentSession = session;
        this.setupSessionHandlers(session);
        this.triggerCallback('incomingCall', session);
      }
    });
  }

  /**
   * Set up event handlers for a call session
   */
  setupSessionHandlers(session) {
    session.on('progress', () => {
      console.log('3CX WebRTC: Call is in progress (ringing)');
      this.triggerCallback('progress');
    });

    session.on('accepted', () => {
      console.log('3CX WebRTC: Call accepted');
      this.triggerCallback('accepted');
    });

    session.on('confirmed', () => {
      console.log('3CX WebRTC: Call confirmed (connected)');
      this.triggerCallback('confirmed');
    });

    session.on('ended', () => {
      console.log('3CX WebRTC: Call ended');
      this.currentSession = null;
      this.triggerCallback('ended');
    });

    session.on('failed', (e) => {
      console.error('3CX WebRTC: Call failed', e);
      this.currentSession = null;
      this.triggerCallback('failed', e);
    });

    // Handle incoming stream
    session.on('peerconnection', (e) => {
      e.peerconnection.onaddstream = (event) => {
        const remoteAudio = new Audio();
        remoteAudio.srcObject = event.stream;
        remoteAudio.play().catch(err => console.error('Audio play error:', err));
      };
    });
  }

  /**
   * Make an outbound call
   * @param {string} phoneNumber - The phone number to call
   * @returns {Promise<Object>} Call details
   */
  async makeCall(phoneNumber) {
    try {
      // Ensure we're registered
      if (!this.isRegistered) {
        await this.initialize();
      }

      // Format phone number
      let formattedNumber = phoneNumber;
      if (!formattedNumber.startsWith('+')) {
        // Assuming Zambia (+260) if no country code
        formattedNumber = `+260${formattedNumber.replace(/^0+/, '')}`;
      }

      // Remove + for SIP URI
      const sipNumber = formattedNumber.replace(/^\+/, '');

      const eventHandlers = {
        progress: () => {
          console.log('3CX WebRTC: Call ringing');
          this.triggerCallback('progress');
        },
        failed: (e) => {
          console.error('3CX WebRTC: Call failed', e);
          this.triggerCallback('failed', e);
        },
        ended: () => {
          console.log('3CX WebRTC: Call ended');
          this.currentSession = null;
          this.triggerCallback('ended');
        },
        confirmed: () => {
          console.log('3CX WebRTC: Call confirmed');
          this.triggerCallback('confirmed');
        },
      };

      const options = {
        eventHandlers,
        mediaConstraints: {
          audio: true,
          video: false,
        },
        pcConfig: {
          iceServers: [
            { urls: ['stun:stun.l.google.com:19302'] },
          ],
        },
      };

      // Make the call
      this.currentSession = this.ua.call(`sip:${sipNumber}@${this.sipServer.replace(/^wss?:\/\//, '').split(':')[0]}`, options);

      // Handle local audio stream
      this.currentSession.connection.addEventListener('addstream', (e) => {
        const remoteAudio = document.createElement('audio');
        remoteAudio.srcObject = e.stream;
        remoteAudio.autoplay = true;
        document.body.appendChild(remoteAudio);
      });

      return {
        success: true,
        session: this.currentSession,
        number: formattedNumber,
      };
    } catch (error) {
      console.error('3CX WebRTC makeCall error:', error);
      throw error;
    }
  }

  /**
   * End the current call
   */
  endCall() {
    if (this.currentSession) {
      this.currentSession.terminate();
      this.currentSession = null;
      return { success: true };
    }
    return { success: false, message: 'No active call' };
  }

  /**
   * Mute the microphone
   */
  mute() {
    if (this.currentSession) {
      this.currentSession.mute();
    }
  }

  /**
   * Unmute the microphone
   */
  unmute() {
    if (this.currentSession) {
      this.currentSession.unmute();
    }
  }

  /**
   * Check if currently in a call
   */
  isInCall() {
    return this.currentSession !== null;
  }

  /**
   * Register callback functions for events
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    this.callbacks[event] = callback;
  }

  /**
   * Trigger registered callbacks
   */
  triggerCallback(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event](data);
    }
  }

  /**
   * Check if WebRTC is configured
   */
  isConfigured() {
    return !!(this.sipServer && this.sipExtension && this.sipPassword);
  }

  /**
   * Unregister and disconnect
   */
  disconnect() {
    if (this.ua) {
      this.ua.stop();
      this.ua = null;
      this.isRegistered = false;
    }
  }
}

// Export singleton instance
const threeCXWebRTC = new ThreeCXWebRTC();
export default threeCXWebRTC;
